<template>
  <div class="locale-message-picker">
    <div class="mode-seg" role="group" aria-label="Message mode">
      <button type="button" class="seg-btn" :class="{ active: !model.localized }" @click="setMode(false)">
        One message <span class="seg-sub">· all locales</span>
      </button>
      <button type="button" class="seg-btn" :class="{ active: model.localized }" @click="setMode(true)">
        Per-locale
      </button>
    </div>

    <!-- Single message (locale-agnostic) -->
    <div v-if="!model.localized" class="single-row">
      <multiselect
        :value="model.single"
        class="rm-select"
        :options="approvedMessages"
        :custom-label="messageLabel"
        track-by="id"
        placeholder="— pick an approved message —"
        :show-labels="false"
        :allow-empty="true"
        :max-height="360"
        @input="setSingle"
      />
    </div>

    <!-- Per-locale messages -->
    <div v-else class="localized-box">
      <div v-if="groupOptions.length" class="group-fill-row">
        <label class="mini-label"><i class="fas fa-layer-group"></i> Fill from a group</label>
        <multiselect
          :value="null"
          class="rm-select group-fill-select"
          :options="groupOptions"
          :custom-label="groupLabel"
          track-by="groupId"
          placeholder="— pick a group to auto-fill its locales —"
          :show-labels="false"
          :max-height="280"
          @input="fillFromGroup"
        />
        <span class="mini-hint">Adds every approved locale in the group; your existing picks are kept.</span>
      </div>

      <div class="locale-rows">
      <div v-for="(entry, idx) in model.entries" :key="`entry-${idx}`" class="locale-row">
        <multiselect
          :value="localeOption(entry.locale)"
          class="rm-select loc-col"
          :options="locales"
          :custom-label="localeLabel"
          track-by="code"
          placeholder="— locale —"
          :show-labels="false"
          :allow-empty="false"
          :max-height="320"
          @input="setEntryLocale(idx, $event)"
        />
        <multiselect
          :value="entry.message"
          class="rm-select msg-col"
          :options="approvedMessages"
          :custom-label="messageLabel"
          track-by="id"
          placeholder="— approved message —"
          :show-labels="false"
          :allow-empty="true"
          :max-height="320"
          @input="setEntryMessage(idx, $event)"
        />
        <button class="icon-remove" title="Remove locale" @click="removeEntry(idx)">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <button class="link-btn" @click="addEntry">
        <i class="fas fa-plus"></i> Add locale
      </button>

      <p v-if="!hasEnUs" class="picker-warn">
        No <code>en-US</code> entry — userLocales without an exact or language match fall back to <code>en-US</code>, so add one as the safe default.
      </p>
      <p v-if="duplicateLocale" class="picker-warn">
        Duplicate locale <code>{{ duplicateLocale }}</code> — only the last one survives in the JSON.
      </p>
      </div>
    </div>
  </div>
</template>

<script>
import { SUPPORTED_LOCALES, localeLabel } from '../../constants/retentionLocales';

const normalize = (v) => {
  const base = { localized: false, single: null, entries: [] };
  if (!v || typeof v !== 'object') return base;
  return {
    localized: !!v.localized,
    single: v.single || null,
    entries: Array.isArray(v.entries) ? v.entries.map((e) => ({ locale: e.locale || '', message: e.message || null })) : []
  };
};

export default {
  name: 'LocaleMessagePicker',
  props: {
    value: { type: Object, default: () => ({ localized: false, single: null, entries: [] }) },
    approvedMessages: { type: Array, default: () => [] },
    // Optional: enables the "Fill from a group" shortcut in per-locale mode.
    groups: { type: Array, default: () => [] },
    membersByGroupId: { type: Object, default: () => ({}) }
  },
  data() {
    return { locales: SUPPORTED_LOCALES, model: normalize(this.value) };
  },
  computed: {
    hasEnUs() {
      return this.model.entries.some((e) => e.locale === 'en-US');
    },
    duplicateLocale() {
      const seen = new Set();
      for (const e of this.model.entries) {
        if (!e.locale) continue;
        if (seen.has(e.locale)) return e.locale;
        seen.add(e.locale);
      }
      return null;
    },
    // Groups with at least one approved, language-bearing member.
    groupOptions() {
      return this.groups
        .map((g) => {
          const members = (this.membersByGroupId[g.groupId] || []).filter((m) => m.language);
          return { groupId: g.groupId, name: g.name || '(unnamed group)', count: members.length, members };
        })
        .filter((g) => g.count > 0)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
  },
  watch: {
    value(v) {
      this.model = normalize(v);
    }
  },
  methods: {
    emitChange() {
      this.$emit('input', {
        localized: this.model.localized,
        single: this.model.single,
        entries: this.model.entries.map((e) => ({ locale: e.locale, message: e.message }))
      });
    },
    localeLabel,
    messageLabel(m) {
      const name = m.name || m.header || '(unnamed)';
      return m.language ? `${name} · ${m.language}` : name;
    },
    localeOption(code) {
      return this.locales.find((l) => l.code === code) || null;
    },
    setMode(localized) {
      if (localized === this.model.localized) return;
      this.model.localized = localized;
      if (localized && this.model.entries.length === 0) {
        // Seed the first row with en-US, carrying over any single selection.
        this.model.entries = [{ locale: 'en-US', message: this.model.single || null }];
      }
      this.emitChange();
    },
    groupLabel(g) {
      return `${g.name} · ${g.count} locale${g.count === 1 ? '' : 's'}`;
    },
    // Augment, never replace: fill empty rows, append missing locales, keep manual picks.
    fillFromGroup(group) {
      if (!group) return;
      const rows = this.model.entries.map((e) => ({ ...e }));
      const byLocale = new Map(rows.map((e) => [e.locale, e]));
      for (const m of group.members) {
        if (!m.language) continue;
        const existing = byLocale.get(m.language);
        if (existing) {
          if (!existing.message) existing.message = m;
        } else {
          const row = { locale: m.language, message: m };
          byLocale.set(m.language, row);
          rows.push(row);
        }
      }
      this.model.entries = rows.filter((e) => e.locale || e.message);
      this.emitChange();
    },
    setSingle(m) {
      this.model.single = m || null;
      this.emitChange();
    },
    setEntryLocale(idx, l) {
      this.model.entries[idx].locale = l ? l.code : '';
      this.emitChange();
    },
    setEntryMessage(idx, m) {
      this.model.entries[idx].message = m || null;
      this.emitChange();
    },
    addEntry() {
      this.model.entries.push({ locale: '', message: null });
      this.emitChange();
    },
    removeEntry(idx) {
      this.model.entries.splice(idx, 1);
      this.emitChange();
    }
  }
};
</script>

<style scoped>
.locale-message-picker {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.mode-seg {
  display: inline-flex;
  align-self: flex-start;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  overflow: hidden;
}
.seg-btn {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.82rem;
  padding: 0.4rem 0.9rem;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}
.seg-btn + .seg-btn { border-left: 1px solid rgba(255, 255, 255, 0.14); }
.seg-btn:hover { background: rgba(255, 255, 255, 0.07); color: #fff; }
.seg-btn.active { background: #03B3DA; color: #fff; }
.seg-sub { opacity: 0.7; font-weight: 400; }
.seg-btn.active .seg-sub { opacity: 0.9; }

.localized-box {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
}
.group-fill-row {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
}
.mini-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
}
.mini-label i { color: #6dd0ea; margin-right: 0.3rem; }
.mini-hint {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
}

.locale-rows {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.locale-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loc-col { width: 220px; min-width: 220px; }
.msg-col { flex: 1; }

.icon-remove {
  background: none;
  border: 1px solid rgba(231, 76, 60, 0.4);
  color: #e74c3c;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  flex-shrink: 0;
}
.icon-remove:hover { background: rgba(231, 76, 60, 0.12); }

.link-btn {
  align-self: flex-start;
  background: none;
  border: none;
  color: #03B3DA;
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.2rem 0;
}
.link-btn:hover { text-decoration: underline; }

.picker-warn {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #ffb84d;
}
.picker-warn code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 5px;
  border-radius: 3px;
  color: #fff;
}

/* vue-multiselect dark theme */
.rm-select ::v-deep .multiselect__tags {
  min-height: 36px;
  padding: 6px 40px 0 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
}
.rm-select ::v-deep .multiselect__single,
.rm-select ::v-deep .multiselect__input {
  background: transparent;
  color: #fff;
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding: 0;
}
.rm-select ::v-deep .multiselect__placeholder {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding-top: 0;
}
/* --active lives on the multiselect root (== .rm-select element), so match it
   as a deep descendant of the component, not under .rm-select. */
::v-deep .multiselect--active .multiselect__tags {
  border-color: #03B3DA;
  box-shadow: 0 0 0 0.15rem rgba(3, 179, 218, 0.2);
}
.rm-select ::v-deep .multiselect__content-wrapper {
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.rm-select ::v-deep .multiselect__option {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  white-space: normal;
  min-height: unset;
  padding: 8px 12px;
}
.rm-select ::v-deep .multiselect__option--highlight,
.rm-select ::v-deep .multiselect__option--highlight::after {
  background: #03B3DA;
  color: #fff;
}
.rm-select ::v-deep .multiselect__option--selected {
  background: rgba(3, 179, 218, 0.18);
  color: #fff;
  font-weight: 600;
}
.rm-select ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #03B3DA;
}
</style>
